
export function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!domain) return "***";
  return `${local[0] ?? ""}***@${domain}`;
}

// ─── Error reporter (dependency injection) ──────────────────────────────────
// Keeps logger free of Sentry imports.  sentry.ts calls registerErrorReporter
// once during init; every subsequent log.error() in production forwards here.
type ErrorReporter = (
  tag: string,
  message: string,
  data?: Record<string, unknown>,
) => void;

let _errorReporter: ErrorReporter | null = null;

/**
 * Register a callback that will be invoked for every log.error() call in
 * production.  Only the first registration is honoured.
 */
export function registerErrorReporter(fn: ErrorReporter): void {
  _errorReporter = fn;
}

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogPayload {
  level: LogLevel;
  tag: string;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
}

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// In production only warn+ gets printed; in dev everything shows.
const MIN_LEVEL: LogLevel = import.meta.env.PROD ? "warn" : "debug";

function shouldLog(level: LogLevel): boolean {
  return LEVEL_PRIORITY[level] >= LEVEL_PRIORITY[MIN_LEVEL];
}


function _send(payload: LogPayload) {
  if (!shouldLog(payload.level)) return;

  // Forward errors to Sentry (production only, no-op until sentryInit() runs)
  if (import.meta.env.PROD && payload.level === "error") {
    _errorReporter?.(payload.tag, payload.message, payload.data);
  }

  const prefix = `[${payload.tag}]`;
  const consoleMethod =
    payload.level === "error"
      ? console.error
      : payload.level === "warn"
        ? console.warn
        : payload.level === "info"
          ? console.info
          : console.debug;

  if (import.meta.env.PROD) {
    consoleMethod(JSON.stringify(payload));
    return;
  }

  if (payload.data && Object.keys(payload.data).length > 0) {
    consoleMethod(prefix, payload.message, payload.data);
  } else {
    consoleMethod(prefix, payload.message);
  }
}


export function createLogger(tag: string) {
  const emit = (level: LogLevel, message: string, data?: Record<string, unknown>) => {
    _send({ level, tag, message, data, timestamp: new Date().toISOString() });
  };

  return {
    debug: (msg: string, data?: Record<string, unknown>) => emit("debug", msg, data),
    info: (msg: string, data?: Record<string, unknown>) => emit("info", msg, data),
    warn: (msg: string, data?: Record<string, unknown>) => emit("warn", msg, data),
    error: (msg: string, data?: Record<string, unknown>) => emit("error", msg, data),
  };
}
